import { driveFormat, driveFormatApp } from './formats';
import type { DriveFileSelection } from './types';

/** The id of an app in the shell's registry. */
export type DriveFileEditor = string;

/**
 * The app that opens a file, from the catalog and the app registry. Null also
 * while the catalog is on its way: ask `driveFormatsSettled` before taking it
 * for "no app".
 */
export function driveFileEditor(name: string): DriveFileEditor | null {
  return driveFormatApp(name)?.id ?? null;
}

/** Reads the one-shot `?drive=<mount>&path=<file>` intent shared by both editors. */
export function parseDriveFileOpen(
  mountId: string,
  companions: Record<string, string>,
): DriveFileSelection | null {
  if (
    !/^\d+$/.test(mountId) ||
    !Number.isSafeInteger(Number(mountId)) ||
    Number(mountId) <= 0
  )
    return null;
  const parts = (companions.path ?? '')
    .replace(/\\/g, '/')
    .split('/')
    .filter((part) => part && part !== '.');
  if (
    !parts.length ||
    parts.some((part) => part === '..' || part.includes('\0'))
  )
    return null;
  const name = parts.at(-1)!;
  const editor = driveFileEditor(name);
  if (!editor) return null;
  return {
    mountId,
    mountName: companions.mountName ?? '',
    path: parts.join('/'),
    name,
    mimeType: driveFormat(name)?.mediaType ?? 'application/octet-stream',
  };
}

/** Router location; the router encodes spaces, separators, Unicode and literal percent signs. */
export function driveFileOpenLocation(
  file: Pick<DriveFileSelection, 'mountId' | 'path' | 'mountName'>,
) {
  const selection = parseDriveFileOpen(file.mountId, {
    path: file.path,
    mountName: file.mountName,
  });
  if (!selection) return null;
  return {
    path: driveFormatApp(selection.name)!.basePath,
    query: {
      drive: selection.mountId,
      path: selection.path,
      mountName: selection.mountName,
    },
  };
}
